import { FC } from "react";
import { Button } from "@mui/material";
import { Add, Check } from "@mui/icons-material";

import { Colors } from "../../../../configs";

import InfoDialog from "../../../atoms/info-dialog/InfoDialog";

interface ITicketDialogBaseProps {
  open: boolean;
  /** Omitted for a ticket that hasn't been checked in yet. */
  ticketNumber?: string | number | null;
  customerName?: string | null;
  onOk(): void;
  /** Overrides the default copy for context-specific wording. */
  message?: string;
}

const ticketLabel = (ticketNumber?: string | number | null) =>
  ticketNumber ? `Ticket #${ticketNumber}` : "New Ticket";

export const TicketNoItemsDialog: FC<ITicketDialogBaseProps> = ({
  open,
  ticketNumber,
  customerName,
  onOk,
  message = "Please add item(s) to this ticket to complete the order.",
}) => (
  <InfoDialog
    open={open}
    testId="ticket-no-items-dialog"
    title="No Items on Ticket"
    highlight={ticketLabel(ticketNumber)}
    subtitle={customerName || undefined}
    message={message}
    okIcon={<Check />}
    onOk={onOk}
  />
);

export interface ITicketMaxItemsDialogProps extends ITicketDialogBaseProps {
  /** Most pieces (qty x piece count) one ticket may hold */
  maxItems: number;
  /** Service the limit is configured on, e.g. "Dry Clean". */
  serviceName?: string;
  /** Shows a "New Ticket" button that starts a new ticket for the customer. */
  onNewTicket?(): void;
}

const dialogButtonSx = {
  minWidth: "8.6rem",
  height: "3rem",
  px: "1.29rem",
  borderRadius: "0.57rem",
  fontSize: "var(--fs-xs)",
  fontWeight: 700,
  textTransform: "none",
} as const;

export const TicketMaxItemsDialog: FC<ITicketMaxItemsDialogProps> = ({
  open,
  ticketNumber,
  customerName,
  onOk,
  maxItems,
  serviceName,
  message,
  onNewTicket,
}) => (
  <InfoDialog
    open={open}
    testId="ticket-max-items-dialog"
    title="Maximum Pieces Reached"
    highlight={ticketLabel(ticketNumber)}
    subtitle={customerName || undefined}
    actions={
      onNewTicket ? (
        <>
          <Button
            disableElevation
            variant="outlined"
            startIcon={<Add />}
            onClick={onNewTicket}
            data-testid="ticket-max-items-dialog-new-ticket"
            sx={{ ...dialogButtonSx, color: Colors.PRIMARY, borderColor: Colors.PRIMARY }}
          >
            New Ticket
          </Button>
          <Button
            autoFocus
            disableElevation
            variant="contained"
            onClick={onOk}
            data-testid="ticket-max-items-dialog-ok"
            sx={{
              ...dialogButtonSx,
              background: Colors.PRIMARY,
              "&:hover": { background: Colors.PRIMARY_DARK },
            }}
          >
            OK
          </Button>
        </>
      ) : undefined
    }
    message={
      message ??
      `A ${serviceName ? `${serviceName} ` : ""}ticket can hold a maximum of ${maxItems} piece${
        maxItems === 1 ? "" : "s"
      }, and this ticket is full. Please close this ticket and add any new items to a new ticket.`
    }
    onOk={onOk}
  />
);
